import { SalesforceAutomation } from 'module/api/integrations/salesforce/types';
import { useTranslate } from 'module/hooks/use-translation';
import { showToast, ToastType } from 'module/ui/components/molecules/toasts';
import { useQueryClient } from '@tanstack/react-query';
import {
  AUTOMATIONS_LIST_QUERY_KEY,
  useDeleteAutomationMutation,
  useSalesForceAutomationsList,
  useUpdateAutomationMutation,
  useReorderAutomationMutation,
} from 'queries/salesforce';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getSalesforceEditAutomationUrl } from 'components/pages/integrations/salesforce/utils/urls';

export const useAutomationsListActions = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { integrationId } = useParams<{ integrationId: string }>();
  const deleteSuccessMessage = translate(
    'settings.integrations.sf.automation.delete.success.message' as any,
  );
  const updateSuccessMessage = translate(
    'settings.integrations.sf.automation.updated.success.message' as any,
  );

  const queryClient = useQueryClient();
  const { data: automationList, status: automationListStatus } =
    useSalesForceAutomationsList();
  const { mutate: updateAutomationMutation } = useUpdateAutomationMutation();
  const { mutate: updateDeleteMutation } = useDeleteAutomationMutation();
  const { mutate: reorderAutomationMutation } = useReorderAutomationMutation();

  const onChange = async (automationId: number, enabled: boolean) => {
    const automation = automationList?.find(
      (a) => a.automationId === automationId,
    );

    if (!automation) {
      return;
    }

    const updatedAutomationDetails = { ...automation, enabled };
    const fallbackValue = automationList?.find(
      (automation) =>
        automation.automationId === updatedAutomationDetails.automationId,
    );

    if (fallbackValue == null) {
      return false;
    }

    // updating local state first
    await queryClient.setQueryData(
      AUTOMATIONS_LIST_QUERY_KEY,
      (savedAutomations) =>
        (savedAutomations as SalesforceAutomation[]).map((automation) => {
          if (
            automation.automationId === updatedAutomationDetails.automationId
          ) {
            return updatedAutomationDetails;
          }

          return automation;
        }),
    );

    return updateAutomationMutation(updatedAutomationDetails, {
      onSuccess: () => {
        showToast({
          toastContainer: { current: document.body },
          content: updateSuccessMessage,
          variant: ToastType.Success,
        });
      },
      onError: () => {
        queryClient.setQueryData(
          AUTOMATIONS_LIST_QUERY_KEY,
          (savedAutomations) =>
            (savedAutomations as SalesforceAutomation[]).map((automation) => {
              if (automation.automationId === fallbackValue.automationId) {
                return fallbackValue;
              }

              return automation;
            }),
        );
      },
    });
  };

  const onDelete = async (automationId: number) => {
    const deletedAutomation = automationList?.find(
      (automation) => automation.automationId === automationId,
    );

    if (deletedAutomation == null) {
      return false;
    }

    // updating local state first
    await queryClient.setQueryData(
      AUTOMATIONS_LIST_QUERY_KEY,
      (savedAutomations) =>
        (savedAutomations as SalesforceAutomation[]).filter(
          (automation) => automation.automationId !== automationId,
        ),
    );

    return updateDeleteMutation(automationId, {
      onSuccess: () => {
        showToast({
          toastContainer: { current: document.body },
          content: deleteSuccessMessage,
          variant: ToastType.Success,
        });
      },
      onError: () => {
        queryClient.setQueryData(
          AUTOMATIONS_LIST_QUERY_KEY,
          (savedAutomations: SalesforceAutomation[] | undefined) => [
            ...(savedAutomations ?? []),
            deletedAutomation,
          ],
        );
      },
    });
  };

  const onEdit = (automationId: number) => {
    if (integrationId) {
      navigate(getSalesforceEditAutomationUrl(integrationId, automationId));
    }
  };

  const onSortChange = async (oldIndex: number, newIndex: number) => {
    if (!automationList || oldIndex === newIndex) {
      return;
    }

    const updatedList = [...automationList];
    const [movedItem] = updatedList.splice(oldIndex, 1);

    updatedList.splice(newIndex, 0, movedItem);

    // Update the order property for each item
    const reorderedList = updatedList.map((item, index) => ({
      ...item,
      order: index,
    }));

    await queryClient.setQueryData(AUTOMATIONS_LIST_QUERY_KEY, reorderedList);

    reorderAutomationMutation(
      {
        automationId: movedItem.automationId!,
        automationUpdatedOrder: newIndex,
      },
      {
        onSuccess: () => {
          showToast({
            toastContainer: { current: document.body },
            content: 'Automation order updated successfully',
            variant: ToastType.Success,
          });
        },
        onError: () => {
          queryClient.setQueryData(AUTOMATIONS_LIST_QUERY_KEY, automationList);

          showToast({
            toastContainer: { current: document.body },
            content: 'Failed to update automation order',
            variant: ToastType.Error,
          });
        },
      },
    );
  };

  const sortedAutomationsList = useMemo(
    () => automationList?.sort((a, b) => a.order - b.order),
    [automationList],
  );

  return {
    automationList: sortedAutomationsList,
    automationListStatus:
      automationListStatus === 'pending' ? 'loading' : automationListStatus,
    onSortChange,
    onDelete,
    onChange,
    onEdit,
  };
};
